/**
 * Alarms resource — typed wrappers over the alarm-event + alarm-rule
 * endpoints plus server→UI mappers.
 *
 *   GET /v1/iot/alarms/events   → AlarmItem[]   (the alarm list)
 *   GET /v1/iot/alarms/rules    → AlarmRule[]   (the Rules tab)
 *
 * Mappers safe-parse the loose `message` / `condition` JSON the server
 * stores and console-warn on malformed payloads rather than throwing.
 */

import { api } from "@/lib/api-client";
import type { AlarmItem } from "@/lib/widgets";
import type { AlarmRule } from "@/lib/alarms/store";
import { buildSpcAlarmCondition, spcAlarmEventInfo } from "@/lib/alarms/spc-condition";
import { qs } from "./qs";
import type {
  AlarmEvent,
  AlarmEventList,
  AlarmRuleCondition,
  AlarmRuleCreateBody,
  AlarmRuleDTO,
  AlarmRuleList,
  AlarmRulePatchBody,
} from "./types";

export const alarms = {
  events: {
    list: (params?: { status?: string; limit?: number; offset?: number }) =>
      api.get<AlarmEventList>(`/v1/iot/alarms/events${qs(params)}`),
  },
  rules: {
    list: (params?: { limit?: number; offset?: number }) =>
      api.get<AlarmRuleList>(`/v1/iot/alarms/rules${qs(params)}`),
    /**
     * Create a rule. `target_tag_id` MUST be a real tag UUID in the active org
     * (the server resolves tag→thing→site→org; a foreign/unknown id → 404
     * `tag_not_found`). Returns the persisted row (201).
     */
    create: (body: AlarmRuleCreateBody) =>
      api.post<AlarmRuleDTO>("/v1/iot/alarms/rules", body),
    /** Partial-update a rule (name/condition/severity/enabled). Returns the updated row. */
    update: (id: string, body: AlarmRulePatchBody) =>
      api.patch<AlarmRuleDTO>(`/v1/iot/alarms/rules/${id}`, body),
    /** Delete a rule (204; 404 if it isn't in the active org). */
    remove: (id: string) => api.delete<void>(`/v1/iot/alarms/rules/${id}`),
  },
};

const SEVERITY_MAP: Record<AlarmEvent["severity"], AlarmItem["severity"]> = {
  info: "info",
  warning: "warning",
  critical: "danger",
};

/**
 * Mapper exists because AlarmItem (the widget row shape) predates the
 * contract. Delete when the AlarmList widget reads AlarmEvent directly.
 */
export function toAlarmItem(e: AlarmEvent): AlarmItem {
  const msg = parseMessage(e.message);
  return {
    id: e.id,
    t: e.fired_at,
    severity: SEVERITY_MAP[e.severity] ?? "info",
    source: msg.source,
    title: msg.title ?? "Alarm",
    description: msg.description,
    acknowledged: e.state === "acknowledged" || e.acknowledged_at != null,
    // Open until it clears: an acknowledged event is still active, and reads "Acknowledged", never "Cleared"
    // (#926 review).
    active: e.state !== "cleared",
    acknowledgedBy:
      e.acknowledged_by && e.acknowledged_at
        ? { user: e.acknowledged_by, at: e.acknowledged_at }
        : undefined,
    // A2 — WHERE the alarm fired. Prefer the backend-resolved location
    // (message.entity_name / stage). `tag_id` is carried so the UI can
    // resolve the location client-side as a fallback when the backend didn't
    // populate it (older API / not-yet-redeployed).
    tagId: e.tag_id ?? undefined,
    entityName: msg.entityName,
    stage: msg.stage,
    // S15: the control chart signal a statistical alarm fired on (absent on any other alarm).
    spc: spcAlarmEventInfo(e.message),
  };
}

function parseMessage(m: unknown): {
  title?: string;
  description?: string;
  source?: string;
  entityName?: string;
  stage?: string;
} {
  if (m && typeof m === "object") {
    const o = m as Record<string, unknown>;
    const str = (v: unknown): string | undefined =>
      typeof v === "string" && v.trim() !== "" ? v : undefined;
    return {
      // New events carry `title`; older ones only `rule_name`.
      title: str(o.title) ?? str(o.rule_name),
      // New events carry the human reason in `body`; older ones carry only
      // the raw `condition` (+ maybe the observed `value`), so reconstruct a
      // reason from those — the UI must never show a blank "why".
      description:
        str(o.body) ??
        str(o.description) ??
        reasonFromCondition(o.condition, o.value),
      source: str(o.source),
      // A2 — the bound twin entity + stage the backend resolved (additive,
      // optional). Absent on tags not bound to any entity or pre-A2 events.
      entityName: str(o.entity_name),
      stage: str(o.stage),
    };
  }
  if (typeof m === "string") return { title: m };
  return {};
}

const OP_PHRASE: Record<string, string> = {
  gt: "above",
  gte: "at or above",
  lt: "below",
  lte: "at or below",
  eq: "equal to",
  ne: "not equal to",
};

/**
 * Fallback reason for alarm events whose message predates the rich `body`
 * (only `rule_name` + `condition` were stored): turn `{op, value}` — plus the
 * observed `value` when present — into "1623 — above 1550" or "above 1550".
 */
function reasonFromCondition(
  condition: unknown,
  observed?: unknown,
): string | undefined {
  if (!condition || typeof condition !== "object") return undefined;
  const c = condition as Record<string, unknown>;
  const op = typeof c.op === "string" ? (OP_PHRASE[c.op] ?? c.op) : "";
  const thr =
    typeof c.value === "number" || typeof c.value === "string"
      ? String(c.value)
      : "";
  if (!op || thr === "") return undefined;
  const obs =
    typeof observed === "number" || typeof observed === "string"
      ? `${observed} — `
      : "";
  return `${obs}${op} ${thr}`;
}

const OP_SET = new Set(["gt", "lt", "gte", "lte", "eq", "ne"]);
type Op = AlarmRule["condition"]["op"];

/**
 * Mapper exists because the client AlarmRule carries UI-only fields
 * (channels, triggers7d, targetLabel) the server doesn't model yet.
 */
export function toAlarmRule(r: AlarmRuleDTO): AlarmRule {
  return {
    id: r.id,
    name: r.name,
    targetTagId: r.target_tag_id,
    condition: parseCondition(r.condition),
    severity: r.severity,
    channels: [],
    enabled: r.enabled,
    triggers7d: 0,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

/**
 * Inverse of `toAlarmRule` — client AlarmRule → POST body. `org_id` is omitted
 * (server takes it from the active-org context). The client condition value is
 * `number | string` (the legacy seed carried e.g. `"5m"`); the contract is
 * `value: number`, so we coerce and fall back to 0 for non-numeric values.
 */
export function toRuleCreateBody(
  rule: Pick<AlarmRule, "name" | "targetTagId" | "condition" | "severity" | "enabled">,
): AlarmRuleCreateBody {
  return {
    name: rule.name,
    target_tag_id: rule.targetTagId,
    condition: toConditionBody(rule.condition),
    severity: rule.severity,
    enabled: rule.enabled,
  };
}

/**
 * Client condition → the server's. A statistical alarm (S15, op "spc") sends only op, chart_id and rules:
 * the server refuses any other key. A threshold condition coerces its value to a number (0 when it is not
 * one) and keeps its debounce.
 */
export function toConditionBody(c: AlarmRule["condition"]): AlarmRuleCondition {
  if (c.op === "spc") return buildSpcAlarmCondition(c.chartId ?? "", c.rules ?? []);
  const value = Number(c.value);
  return {
    op: c.op,
    value: Number.isFinite(value) ? value : 0,
    ...(c.forSeconds != null ? { forSeconds: c.forSeconds } : {}),
  };
}

/**
 * Build a PATCH body from a partial client-side rule patch. Mirrors
 * `toRuleCreateBody` but every field is optional + `target_tag_id` is dropped
 * (the patch contract can't re-target a rule). `undefined` fields are omitted
 * so the server's COALESCE leaves them untouched.
 */
export function toRulePatchBody(patch: Partial<AlarmRule>): AlarmRulePatchBody {
  const body: AlarmRulePatchBody = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.severity !== undefined) body.severity = patch.severity;
  if (patch.enabled !== undefined) body.enabled = patch.enabled;
  if (patch.condition !== undefined) body.condition = toConditionBody(patch.condition);
  return body;
}

function parseCondition(c: unknown): AlarmRule["condition"] {
  if (c && typeof c === "object") {
    const o = c as Record<string, unknown>;
    // S15: a statistical alarm keeps its chart and rules (read as a threshold it would be saved back as one).
    if (o.op === "spc") {
      const rules = Array.isArray(o.rules) ? o.rules.filter((r): r is string => typeof r === "string") : [];
      return {
        op: "spc",
        value: "",
        chartId: typeof o.chart_id === "string" ? o.chart_id : "",
        ...(rules.length > 0 ? { rules } : {}),
      };
    }
    const op: Op =
      typeof o.op === "string" && OP_SET.has(o.op) ? (o.op as Op) : "gt";
    const value =
      typeof o.value === "number" || typeof o.value === "string"
        ? o.value
        : 0;
    const forSeconds =
      typeof o.forSeconds === "number" ? o.forSeconds : undefined;
    return { op, value, forSeconds };
  }
  console.warn(
    "[api/alarms] malformed condition payload, defaulting to { gt, 0 }:",
    c,
  );
  return { op: "gt", value: 0 };
}
